import { text } from '#contract'
import type { LocalizedText } from '#contract'
import { assert } from '#shared'
import {
  FilesIcon,
  GrubFoodIcon,
  LockedInIcon,
  PhoneIcon,
  TwentyFourSevenIcon,
  WassupIcon,
  WellfareIcon,
} from '../icons/app-icons.tsx'
import type { OsForm } from '../os-state.ts'
import { FilesApp } from './files.tsx'
import { GrubFoodApp } from './grubfood.tsx'
import { LockedInApp } from './lockedin.tsx'
import { PhoneApp } from './phone.tsx'
import { TwentyFourSevenApp } from './twentyfourseven.tsx'
import type { OsApp } from './types.ts'
import { WassupApp } from './wassup/wassup-app.tsx'
import { WellfareApp } from './wellfare/wellfare-app.tsx'

export type { OsApp } from './types.ts'

/** Every app, in launcher order. Adding one: an entry here, a component, an icon, two strings. */
const OS_APPS: readonly OsApp[] = [
  {
    id: 'wassup',
    forms: ['phone', 'desktop'],
    pinned: true,
    lightStatus: true,
    icon: WassupIcon,
    component: WassupApp,
    badge: (view) => view.messages.unread,
  },
  {
    id: 'lockedin',
    forms: ['phone', 'desktop'],
    pinned: true,
    icon: LockedInIcon,
    component: LockedInApp,
  },
  {
    id: 'grubfood',
    forms: ['phone', 'desktop'],
    pinned: true,
    lightStatus: true,
    icon: GrubFoodIcon,
    component: GrubFoodApp,
  },
  {
    id: '24seven',
    forms: ['phone', 'desktop'],
    icon: TwentyFourSevenIcon,
    component: TwentyFourSevenApp,
  },
  { id: 'wellfare', forms: ['desktop'], icon: WellfareIcon, component: WellfareApp },
  { id: 'files', forms: ['desktop'], icon: FilesIcon, component: FilesApp },
  { id: 'phone', forms: ['phone'], icon: PhoneIcon, component: PhoneApp },
]

// One stable list per form, so components can select it without rebuilding it.
const BY_FORM: Readonly<Record<OsForm, readonly OsApp[]>> = {
  phone: OS_APPS.filter((app) => app.forms.includes('phone')),
  desktop: OS_APPS.filter((app) => app.forms.includes('desktop')),
}

/** The apps that run on `form`, in launcher order. */
export function appsFor(form: OsForm): readonly OsApp[] {
  return BY_FORM[form]
}

export function appById(id: string): OsApp {
  const app = OS_APPS.find((candidate) => candidate.id === id)
  assert(app, `No OS app "${id}"`)
  return app
}

export function appName(id: string): LocalizedText {
  return text(`os.app.${id}.name`)
}
